import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle, XCircle, Buildings, Lightning, Cpu, Cloud, ShieldCheck, Target } from "@phosphor-icons/react";

const edges = [
  {
    icon: Cpu,
    title: "AI-First Architecture",
    body: "Systems designed around intelligence, not retrofitted onto legacy IT.",
    tint: "bg-sky-50",
    accent: "bg-sky-500",
    text: "text-sky-700",
  },
  {
    icon: Cloud,
    title: "Cloud-Native Foundation",
    body: "Scalable, secure, and future-ready infrastructure from day one.",
    tint: "bg-sky-50",
    accent: "bg-sky-600",
    text: "text-sky-700",
  },
  {
    icon: Buildings,
    title: "Deep Industry Expertise",
    body: "Domain-specific playbooks across metals, mining, manufacturing, and more.",
    tint: "bg-blue-50",
    accent: "bg-blue-500",
    text: "text-blue-700",
  },
  {
    icon: Lightning,
    title: "Rapid Implementation",
    body: "Featured platforms engineered for accelerated time-to-value.",
    tint: "bg-sky-50",
    accent: "bg-sky-400",
    text: "text-sky-700",
  },
  {
    icon: Target,
    title: "Measurable Outcomes",
    body: "Quantified productivity, cost, and growth improvements — not vanity metrics.",
    tint: "bg-blue-50",
    accent: "bg-blue-600",
    text: "text-blue-700",
  },
  {
    icon: ShieldCheck,
    title: "24×7 Intelligent Operations",
    body: "Continuous monitoring, decision support, and autonomous workflows.",
    tint: "bg-sky-50",
    accent: "bg-sky-500",
    text: "text-sky-700",
  },
];

const past = [
  "Legacy IT bolted onto operational silos",
  "Reactive maintenance and downtime",
  "Fragmented data across systems",
  "Vendor-driven, one-size-fits-all packages",
  "Manual reporting cycles",
];

const future = [
  "AI-first, cloud-native architecture",
  "Predictive maintenance and autonomy",
  "Unified data lake and command center",
  "Industry-specific, modular platforms",
  "Real-time operations intelligence",
];

export default function About() {
  return (
    <div data-testid="page-about">
      {/* HERO */}
      <section className="border-b border-zinc-200" data-testid="about-hero">
        <div className="container-x mx-auto py-20 lg:py-32">
          <div className="text-overline mb-6">Why Bizlem</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            Transforming industries through intelligence.
          </h1>
          <p className="mt-8 text-xl leading-relaxed text-zinc-600 max-w-3xl">
            Bizlem empowers industrial enterprises with intelligent, sustainable, and
            future-ready solutions — an end-to-end engine for continuous competitive advantage.
          </p>
        </div>
      </section>

      {/* BIZLEM EDGE */}
      <section className="border-b border-zinc-200" data-testid="bizlem-edge">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-5">
              <div className="text-overline mb-4">The Bizlem Edge</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
                Comprehensive capabilities driving operational and strategic intelligence.
              </h2>
            </div>
            <p className="lg:col-span-7 text-lg text-zinc-600 max-w-2xl lg:pt-16">
              A layered, integrated approach — from architectural foundations to actionable
              enterprise suites — designed for heavy industry.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {edges.map((e, i) => (
              <div
                key={e.title}
                data-testid={`edge-${i}`}
                className={`${e.tint} border border-zinc-200 p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group relative overflow-hidden`}
              >
                <div className={`absolute top-0 left-0 h-1 w-full ${e.accent}`} />
                <div className="flex items-start justify-between">
                  <div className={`inline-flex items-center justify-center w-14 h-14 ${e.accent} text-white`}>
                    <e.icon size={26} weight="light" />
                  </div>
                  <span className={`text-overline ${e.text}`}>0{i + 1}</span>
                </div>
                <h3 className="mt-8 text-xl font-bold">{e.title}</h3>
                <p className="mt-3 text-zinc-700 leading-relaxed">{e.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DESIGNING THE FUTURE VS PATCHING THE PAST */}
      <section className="border-b border-sky-200 bg-sky-50/50" data-testid="future-vs-past">
        <div className="container-x mx-auto py-20 lg:py-32">
          <div className="max-w-3xl mb-16">
            <div className="text-overline mb-4">Design Philosophy</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
              Designing the future vs. patching the past.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border-2 border-zinc-300 p-8 lg:p-12 opacity-80">
              <div className="text-overline text-zinc-500 mb-4">Patching the Past</div>
              <h3 className="text-2xl font-bold text-zinc-500 line-through decoration-2 mb-8">
                Legacy digital.
              </h3>
              <ul className="space-y-4">
                {past.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-zinc-600">
                    <XCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-sky-300" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-sky-50 border-2 border-sky-500 p-8 lg:p-12 shadow-lg relative">
              <div className="absolute top-0 right-0 bg-sky-500 text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1">Recommended</div>
              <div className="text-overline text-sky-700 mb-4">Designing the Future</div>
              <h3 className="text-2xl font-bold mb-8 text-zinc-950">The Bizlem approach.</h3>
              <ul className="space-y-4">
                {future.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-zinc-900">
                    <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-sky-600" />
                    <span className="font-medium">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-200" data-testid="about-cta">
        <div className="container-x mx-auto py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter max-w-3xl">
              Ready to transform your business?
            </h2>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="about-cta-book-btn"
                className="inline-flex items-center gap-2 bg-sky-500 text-white px-6 py-4 text-sm font-semibold hover:bg-sky-600 transition-colors"
              >
                Book a Consultation <ArrowUpRight size={16} weight="bold" />
              </Link>
              <Link
                to="/platforms"
                data-testid="about-cta-platforms-btn"
                className="inline-flex items-center gap-2 border border-sky-500 text-sky-700 px-6 py-4 text-sm font-semibold hover:bg-sky-500 hover:text-white transition-colors"
              >
                View Platforms
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
